<template>
  <v-card
    class="sub-agent-panel"
    :class="{ 'sub-agent-panel--resolver': subAgent.key === 'resolver-cvc' }"
    outlined
  >
    <div class="sub-agent-panel-header" @click="toggleExecutions">
      <div class="sub-agent-panel-icon">
        <v-icon size="20" color="#384049">{{ subAgent.icon }}</v-icon>
      </div>
      <div class="sub-agent-panel-title">
        <div class="name-row">
          <span class="name">{{ subAgent.name }}</span>
          <StatusChip v-if="statusKey !== 'sucesso'" :status="statusKey" :text="statusText" />
        </div>
        <span class="description">{{ subAgent.description }}</span>
      </div>
      <v-icon size="20" class="chevron" :class="{ 'is-open': showExecutions }">
        mdi-chevron-down
      </v-icon>
    </div>

    <div class="sub-agent-panel-facts">
      <div class="fact">
        <span class="fact-label">Última execução</span>
        <span class="fact-value">{{ lastExecLabel }}</span>
      </div>
      <div v-if="subAgent.nextRun" class="fact">
        <span class="fact-label">Próxima execução</span>
        <span class="fact-value">{{ subAgent.nextRun }}</span>
      </div>
      <div v-if="subAgent.frequency" class="fact">
        <span class="fact-label">Frequência</span>
        <span class="fact-value">{{ subAgent.frequency }}</span>
      </div>
      <v-btn
        class="run-button"
        small
        :outlined="!runButtonBusy"
        :depressed="runButtonBusy"
        :disabled="disabled || runButtonBusy"
        color="#0f7a6e"
        :dark="runButtonBusy"
        @click.stop="startRun"
      >
        <v-icon size="16" left :class="{ 'spin-icon': runButtonBusy }">
          {{ runButtonBusy ? "mdi-loading" : "mdi-play-circle-outline" }}
        </v-icon>
        {{ runButtonLabel }}
      </v-btn>
    </div>

    <p v-if="runError" class="sub-agent-panel-run-error">
      <v-icon size="14" color="#c81e22">mdi-alert-circle-outline</v-icon>
      {{ runError }}
    </p>

    <!-- So o pipeline pergunta periodo — o resolver nao depende mais de data
         nenhuma (fila de pendencia por voucher, nao por intervalo). -->
    <v-dialog v-model="periodDialog" max-width="420" persistent>
      <v-card>
        <v-card-title class="period-dialog-title">Qual período o agente deve rodar?</v-card-title>
        <v-card-text>
          <v-date-picker
            v-model="periodRange"
            range
            no-title
            locale="pt-br"
            color="#0f7a6e"
            width="100%"
          />
          <p v-if="periodExcedeLimite" class="period-dialog-error">
            <v-icon size="14" color="#c81e22">mdi-alert-circle-outline</v-icon>
            Período de {{ periodDias }} dias excede o máximo de
            {{ maxDiasPipeline }} dias por execução.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="periodDialog = false">Cancelar</v-btn>
          <v-btn
            color="#0f7a6e"
            dark
            depressed
            :disabled="periodRange.length < 2 || periodExcedeLimite"
            @click="confirmRunWithPeriod"
          >
            Executar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <button class="steps-toggle" @click.stop="showSteps = !showSteps">
      <v-icon size="14">{{ showSteps ? "mdi-eye-off-outline" : "mdi-format-list-bulleted" }}</v-icon>
      {{ showSteps ? "Ocultar etapas do pipeline" : "Ver etapas do pipeline" }}
    </button>
    <v-expand-transition>
      <div v-if="showSteps" class="sub-agent-panel-steps">
        <PipelineSteps :steps="subAgent.steps" :current-step-index="subAgent.currentStepIndex" />
      </div>
    </v-expand-transition>

    <v-expand-transition>
      <div v-if="isRunningLive" class="sub-agent-panel-live">
        <div class="live-title">
          <span class="live-badge">AO VIVO</span>
          Execução em andamento
        </div>
        <LogTimeline :logs="liveLogs" />
      </div>
    </v-expand-transition>

    <v-expand-transition>
      <div v-if="showExecutions" class="sub-agent-panel-executions">
        <div class="executions-title">Execuções no período</div>
        <p v-if="loadingExecutions" class="executions-empty">Carregando execuções…</p>
        <template v-else-if="localExecutions.length">
          <ExecutionRow
            v-for="execution in localExecutions"
            :key="execution.id"
            :execution="execution"
            :open="openExecutionId === execution.id"
            :loading="loadingLogsId === execution.id"
            @toggle="toggleExecution(execution.id)"
          />
        </template>
        <p v-else class="executions-empty">Nenhuma execução no período selecionado.</p>
      </div>
    </v-expand-transition>
  </v-card>
</template>

<script>
import StatusChip from "@/components/StatusChip/StatusChip.vue";
import PipelineSteps from "@/views/Dashboard/Partials/PipelineSteps/PipelineSteps.vue";
import ExecutionRow from "@/views/Dashboard/Partials/ExecutionRow/ExecutionRow.vue";
import LogTimeline from "@/views/Dashboard/Partials/LogTimeline/LogTimeline.vue";
import { lastExecutionLabel } from "@/data/mockData.js";
import { getExecutionLogs, getExecutions, runAgent } from "@/services/nexiaApi.js";

const STATUS_TEXT = {
  andamento: "Em execução",
  sucesso: "Disponível",
  erro: "Erro",
};

// Trava de periodo do pipeline — mesmo limite validado no backend
// (api/src/routes/agents.js). Ranges grandes sobrecarregam a fila de
// importacao do Phoenix (ver comentario em pipeline.mjs sobre o teste de
// 5 dias/272 reservas que estourou o timeout antigo).
const MAX_DIAS_PIPELINE = 7;

export default {
  name: "SubAgentPanel",
  components: { StatusChip, PipelineSteps, ExecutionRow, LogTimeline },
  props: {
    subAgent: { type: Object, required: true },
    executions: { type: Array, default: () => [] },
    range: { type: Object, required: true },
    disabled: { type: Boolean, default: false },
    base: { type: String, default: null },
  },
  data() {
    return {
      localExecutions: [...this.executions],
      showExecutions: false,
      showSteps: false,
      openExecutionId: null,
      loadingLogsId: null,
      loadingExecutions: false,
      isRunningLive: false, // ha execucao 'andamento' real desse sub-agente
      requested: false, // clicamos "Executar agora" e esperamos a execucao aparecer
      runError: "",
      liveLogs: [],
      runningExecId: null,
      preRunExecIds: [],
      pollTimer: null,
      pollStartedAt: 0,
      periodDialog: false,
      periodRange: [this.todayIso(), this.todayIso()],
    };
  },
  computed: {
    isPipeline() {
      return this.subAgent.key === "pipeline-cvc";
    },
    lastKnownStatus() {
      const last = this.localExecutions[0];
      return last && last.status === "erro" ? "erro" : "sucesso";
    },
    statusKey() {
      return this.isRunningLive || this.requested ? "andamento" : this.lastKnownStatus;
    },
    statusText() {
      return STATUS_TEXT[this.statusKey] || this.statusKey;
    },
    lastExecLabel() {
      return lastExecutionLabel(this.localExecutions);
    },
    runButtonBusy() {
      return this.requested || this.isRunningLive;
    },
    runButtonLabel() {
      if (this.requested && !this.isRunningLive) return "Aguardando…";
      if (this.isRunningLive) return "Executando…";
      return "Executar agora";
    },
    periodDias() {
      if (this.periodRange.length < 2) return 0;
      const [a, b] = [...this.periodRange].sort();
      const ms = new Date(`${b}T00:00:00Z`) - new Date(`${a}T00:00:00Z`);
      return Math.round(ms / 86400000) + 1;
    },
    periodExcedeLimite() {
      return this.periodRange.length >= 2 && this.periodDias > MAX_DIAS_PIPELINE;
    },
    maxDiasPipeline() {
      return MAX_DIAS_PIPELINE;
    },
  },
  watch: {
    executions(newVal) {
      // Nao sobrescreve enquanto o poll local esta acompanhando um run vivo.
      if (this.pollTimer) return;
      this.localExecutions = [...newVal];
      this.openExecutionId = null;
    },
    base() {
      // Trocar a base com uma execucao sendo acompanhada ao vivo deixava o
      // poll preso rastreando o runningExecId/preRunExecIds da base antiga
      // -- os logs "em tempo real" ficavam presos em Recife (ou qualquer
      // base anterior) ate recarregar a pagina (achado em 2026-09-17). Reseta
      // o acompanhamento ao vivo; a lista sincroniza de novo pelo watch acima.
      this.stopPolling();
      this.requested = false;
      this.isRunningLive = false;
      this.runningExecId = null;
      this.liveLogs = [];
      this.preRunExecIds = [];
      this.runError = "";
      this.openExecutionId = null;
      this.localExecutions = [...this.executions];
      // Reconecta ao "ao vivo" se a base pra onde acabamos de trocar ja
      // tiver uma execucao em andamento (ex.: Recife rodando, foi pra Natal
      // e voltou) -- sem isso o painel "ao vivo" ficava fechado ate a run
      // terminar, mesmo com ela realmente em andamento.
      this.syncLiveState();
    },
  },
  mounted() {
    // Mesmo caso do watch de base, mas pro carregamento inicial da pagina:
    // sem isso, uma execucao ja em andamento so virava "ao vivo" se o botao
    // fosse clicado NESTA sessao -- reload/primeira carga nunca reconectava.
    this.syncLiveState();
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    // Busca o estado atual da base e reconecta ao "ao vivo" se ja houver
    // uma execucao 'andamento' -- chamado no mount e sempre que `base` troca
    // (ver watch acima). Sem isso, "ao vivo" so aparecia se o botao fosse
    // clicado nesta mesma sessao/aba.
    async syncLiveState() {
      let execs;
      try {
        execs = await getExecutions(this.subAgent.key, this.range, this.base);
      } catch (e) {
        return; // tenta de novo na proxima troca/poll natural
      }
      this.localExecutions = execs;
      const emAndamento = execs.find((e) => e.status === "andamento");
      if (!emAndamento) return;

      this.runningExecId = emAndamento.id;
      this.isRunningLive = true;
      try {
        this.liveLogs = await getExecutionLogs(emAndamento.id);
      } catch (e) {
        this.liveLogs = [];
      }
      this.startPolling();
    },
    toggleExecutions() {
      this.showExecutions = !this.showExecutions;
      if (!this.showExecutions) {
        this.openExecutionId = null;
      }
    },
    async toggleExecution(id) {
      if (this.openExecutionId === id) {
        this.openExecutionId = null;
        return;
      }
      this.openExecutionId = id;

      const exec = this.localExecutions.find((e) => e.id === id);
      if (!exec || exec.logs) return;

      this.loadingLogsId = id;
      try {
        const logs = await getExecutionLogs(id);
        this.$set(exec, "logs", logs);
      } catch (err) {
        console.error("Falha ao carregar logs da execução", err);
        this.$set(exec, "logs", []);
      } finally {
        this.loadingLogsId = null;
      }
    },
    todayIso() {
      const d = new Date();
      const p = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
    },
    isoToBr(iso) {
      const [y, m, d] = iso.split("-");
      return `${d}/${m}/${y}`;
    },
    startRun() {
      if (this.disabled || this.runButtonBusy) return;
      if (this.isPipeline) {
        // Reabre sempre em "hoje" — nao deixa um periodo antigo escolhido
        // numa execucao anterior vazar pra proxima sem o usuario perceber.
        this.periodRange = [this.todayIso(), this.todayIso()];
        this.periodDialog = true;
        return;
      }
      this.doRun();
    },
    confirmRunWithPeriod() {
      if (this.periodRange.length < 2 || this.periodExcedeLimite) return;
      const [a, b] = [...this.periodRange].sort();
      this.periodDialog = false;
      this.doRun({ dataInicial: this.isoToBr(a), dataFinal: this.isoToBr(b) });
    },
    async doRun(period) {
      this.runError = "";
      try {
        await runAgent(this.subAgent.key, this.base, period);
        // Marca quais execucoes ja existiam — assim o poll reconhece a NOVA.
        this.preRunExecIds = this.localExecutions.map((e) => e.id);
        this.requested = true;
        this.showExecutions = true;
        this.$emit("run-start", this.subAgent.key);
        this.startPolling();
      } catch (err) {
        const msg =
          (err && err.response && err.response.data && err.response.data.error) ||
          "Falha ao solicitar execução";
        this.runError = msg;
        setTimeout(() => {
          this.runError = "";
        }, 6000);
      }
    },
    startPolling() {
      this.stopPolling();
      this.pollStartedAt = Date.now();
      this.pollTimer = setInterval(this.pollTick, 5000);
      this.pollTick();
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    async pollTick() {
      // Trava de seguranca: nao acompanha pra sempre se algo travar.
      if (Date.now() - this.pollStartedAt > 25 * 60 * 1000) {
        this.stopPolling();
        this.requested = false;
        this.isRunningLive = false;
        this.$emit("run-end", this.subAgent.key);
        return;
      }
      let execs;
      try {
        execs = await getExecutions(this.subAgent.key, this.range, this.base);
      } catch (e) {
        return; // tenta de novo no proximo tick
      }
      this.localExecutions = execs;

      // Enquanto 'requested', so interessa a execucao NOVA (criada apos o clique).
      // Depois que virou 'live', segue a execucao que ja identificamos.
      const isNew = (e) => !this.preRunExecIds.includes(e.id);
      const alvo = this.requested
        ? execs.find(isNew)
        : execs.find((e) => e.id === this.runningExecId);

      if (alvo && alvo.status === "andamento") {
        this.requested = false;
        this.isRunningLive = true;
        this.runningExecId = alvo.id;
        try {
          this.liveLogs = await getExecutionLogs(alvo.id);
        } catch (e) {
          /* mantem os logs que ja tinha */
        }
        return;
      }
      if (alvo && alvo.status !== "andamento") {
        // A execucao apareceu e ja terminou (foi acompanhada ou rapida demais
        // pra pegar em 'andamento'). Fecha o estado do botao.
        this.stopPolling();
        this.requested = false;
        this.isRunningLive = false;
        this.runningExecId = null;
        this.liveLogs = [];
        this.$emit("run-end", this.subAgent.key);
        return;
      }
      // 'requested' e nenhuma execucao nova apareceu ainda: espera ate 6 min.
      // O caminho e assincrono — poll do agente + criacao do job + latencia
      // do LLM + spin-up da sessao antes do script emitir o primeiro evento.
      // Passou de 6 min, o job provavelmente nao rodou — libera o botao.
      if (this.requested && Date.now() - this.pollStartedAt > 6 * 60 * 1000) {
        this.stopPolling();
        this.requested = false;
        this.runError =
          "Execução solicitada, mas o agente não iniciou em 6 min.";
        setTimeout(() => {
          this.runError = "";
        }, 10000);
        this.$emit("run-end", this.subAgent.key);
      }
    },
  },
};
</script>

<style src="./styles.scss" scoped lang="scss" />
